// What the aligner puts in the shell's heading bar (src/app/TopBar.tsx): the wordmark goes back to
// the open screen, as the shell's goes to Projects, and is followed by the aligner's name; the
// middle names the documents aligned, or the session file or TMX the session came from; the right
// holds the save state, which the shell shows in its status bar, in the shell's words. The
// aligner has no sign-in, so nothing of the shell's account or sections is here.

import { TopBar } from '../../app/TopBar.tsx'
import type { AutosaveNotice } from '../engine/client.ts'
import type { Opened } from '../engine/protocol.ts'

interface Props {
  opened: Opened
  autosave: AutosaveNotice | null
  /// A change the autosave has not caught up with yet.
  saving: boolean
  onHome: () => void
}

export function AlignBar({ opened, autosave, saving, onHome }: Props) {
  return (
    <TopBar
      home={{ 'aria-label': 'Interplae Align, open other documents', title: 'Open other documents. The session stays autosaved.', onClick: onHome }}
      name="Align"
      title={<Documents opened={opened} />}
      right={
        // Later: the way back to the person's project pane in the main app
        // (docs/design/aligner/decisions.md, "Later") goes after the save state, as the shell's
        // Close project does.
        <Saved autosave={autosave} saving={saving} />
      }
    />
  )
}

/// The documents' names: the source files, then the target files, each side's first named and the
/// rest counted, with every name in the tooltip.
function Documents({ opened }: { opened: Opened }) {
  if (opened.kind === 'file') {
    return <h1 className="doc" lang={titleLang(opened.name)} title={opened.name}>{opened.name}</h1>
  }
  const side = (names: string[]) => {
    const first = names[0] ?? ''
    return (
      <span className="docside" lang={titleLang(first)}>
        {first}
        {names.length > 1 ? <span className="more"> and {names.length - 1} more</span> : null}
      </span>
    )
  }
  return (
    <h1 className="doc" title={`${opened.source.join(', ')} → ${opened.target.join(', ')}`}>
      {side(opened.source)}
      <span className="arrow" aria-label="aligned with"> → </span>
      {side(opened.target)}
    </h1>
  )
}

/// The save state in the shell's words: the autosave keeps the session in this browser, a
/// moment after each change.
function Saved({ autosave, saving }: { autosave: AutosaveNotice | null; saving: boolean }) {
  if (autosave === null) return <span className="saving" data-saved="pending">Saving on this device</span>
  if ('error' in autosave) return <span className="alert" data-saved="failed" title={autosave.error}>Autosave failed</span>
  if (saving) return <span className="saving" data-saved="saving">Saving on this device</span>
  const at = new Date(autosave.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return <span className="saved" data-saved="saved" title={`Autosaved in this browser at ${at}`}>Saved on this device</span>
}

const titleLang = (text: string): 'th' | undefined => (/[฀-๿]/.test(text) ? 'th' : undefined)
